/**
 * Contractual material traceability for a job. CR-2026-TRC-GATE (2026-10-13).
 * Server: GET / PUT /api/job/:id/material-traceability (sfm-web-backend src/routes/jobTraceability.js).
 *
 * Traceability is contractual and agreed per job. Until it is recorded, the server refuses Release for
 * Fabrication, stage moves and station work on the job. Heat to mark / heat to piece mark then need a heat
 * link on every piece before it is released or worked. Setting it needs edit_job; the contract reference is
 * required, a change needs a reason, and every change is kept in an append-only history.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup,
  Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";

import SmartTable from "../SmartTable/SmartTable";
export const TRACEABILITY_LEVELS = [
  { value: "NOT_REQUIRED", label: "Not required by contract", help: "MTRs on file per material; no heat link per piece." },
  { value: "HEAT_TO_MARK", label: "Heat to mark", help: "Every mark carries its heat link before release or station work." },
  { value: "HEAT_TO_PIECE", label: "Heat to piece mark (PMK)", help: "Every individual piece mark carries its own heat link." },
];
export const TRACEABILITY_LABEL = {
  UNSET: "Not recorded", NOT_REQUIRED: "Not required by contract", HEAT_TO_MARK: "Heat to mark", HEAT_TO_PIECE: "Heat to piece mark (PMK)",
};
const CHIP = {
  UNSET: { bg: "#FBE9E9", color: "#8A1C1C" },
  NOT_REQUIRED: { bg: "#EEF1F5", color: "#141A24" },
  HEAT_TO_MARK: { bg: "#E7F1FF", color: "#0B4F99" },
  HEAT_TO_PIECE: { bg: "#E6F2EA", color: "#1F5E3A" },
};

/** The PUT body (exported for tests). */
export function traceabilityBody(level, contractRef, reason) {
  const b = { level, contractRef: (contractRef || "").trim() };
  if ((reason || "").trim()) b.reason = reason.trim();
  return b;
}

/** The first message the server would give, or null (mirrors the server's checks for a faster answer). */
export function traceabilityProblem(current, level, contractRef, reason) {
  if (!TRACEABILITY_LEVELS.some((l) => l.value === level)) return "Choose what the contract requires.";
  if ((contractRef || "").trim().length < 2) return "Give the contract reference that states the requirement.";
  if (current && current !== "UNSET" && (reason || "").trim().length < 4) return "Give the reason for changing the recorded requirement (at least 4 characters).";
  return null;
}

const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

export default function MaterialTraceabilityControl({ jobId }) {
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canEdit = hasPermission(perms, PERMISSIONS.EDIT_JOB);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState("");
  const [ref, setRef] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await ApiCall.get(`/job/${jobId}/material-traceability`);
      setData(r.data.data); setLoadError(null);
    } catch (e) { setLoadError(msgOf(e, "Could not load.")); }
  }, [jobId]);
  useEffect(() => { if (jobId) load(); }, [jobId, load]);

  const openDialog = () => {
    setLevel(data?.level && data.level !== "UNSET" ? data.level : "");
    setRef(data?.contract_ref || ""); setReason(""); setError(null); setOpen(true);
  };
  const save = async () => {
    const p = traceabilityProblem(data?.level, level, ref, reason);
    if (p) { setError(p); return; }
    setBusy(true); setError(null);
    try {
      const r = await ApiCall.put(`/job/${jobId}/material-traceability`, traceabilityBody(level, ref, reason));
      setData(r.data.data); setOpen(false);
    } catch (e) { setError(msgOf(e, "Not recorded.")); } finally { setBusy(false); }
  };

  if (loadError) return <Typography variant="caption" color="error">{loadError}</Typography>;
  if (!data) return <Typography variant="caption">Loading</Typography>;
  const c = CHIP[data.level] || CHIP.UNSET;
  const changing = data.level !== "UNSET";

  return (
    <>
      <Stack spacing={0.5} alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={TRACEABILITY_LABEL[data.level] || data.level} sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600 }} />
          {canEdit && <Button size="small" onClick={openDialog}>{changing ? "Change" : "Record"}</Button>}
        </Stack>
        {data.level === "UNSET" ? (
          <Typography variant="caption" sx={{ color: "#8A1C1C" }}>Fabrication is blocked until the contract requirement is recorded.</Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {data.contract_ref} · {data.set_by_name || "user"} · {String(data.set_at || "").slice(0, 10)}
          </Typography>
        )}
      </Stack>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Material traceability required by the contract</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Record what the contract requires for this job. Heat to mark and heat to piece mark block release and station
            work on any piece without a heat link. The record, its reference and every change are kept.
          </Typography>
          <RadioGroup value={level} onChange={(e) => setLevel(e.target.value)}>
            {TRACEABILITY_LEVELS.map((l) => (
              <FormControlLabel key={l.value} value={l.value} control={<Radio />} disabled={busy}
                label={<span><strong>{l.label}</strong><br /><Typography component="span" variant="caption" color="text.secondary">{l.help}</Typography></span>} />
            ))}
          </RadioGroup>
          {level === "HEAT_TO_PIECE" && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Every individual piece needs its own heat link before release or station work. Issue heats to pieces on each heat's page.
            </Alert>
          )}
          <TextField fullWidth size="small" sx={{ mt: 2 }} label="Contract reference (spec section, PO clause)" value={ref}
            onChange={(e) => setRef(e.target.value)} disabled={busy} required />
          {changing && (
            <TextField fullWidth size="small" sx={{ mt: 2 }} label="Reason for the change" value={reason} multiline minRows={2}
              onChange={(e) => setReason(e.target.value)} disabled={busy} required />
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {data.history?.length > 0 && (
            <SmartTable tableId="traceability-history" lockedColumns={["Date", "Change"]} size="small" sx={{ mt: 2 }}>
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Change</TableCell><TableCell>Reference</TableCell><TableCell>By</TableCell></TableRow></TableHead>
              <TableBody>
                {data.history.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>{String(h.changed_at || "").slice(0, 10)}</TableCell>
                    <TableCell>{TRACEABILITY_LABEL[h.from_level]} to {TRACEABILITY_LABEL[h.to_level]}{h.reason ? ` (${h.reason})` : ""}</TableCell>
                    <TableCell>{h.contract_ref}</TableCell>
                    <TableCell>{h.changed_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !level}>Record</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
